import {
	CheckboxButton,
	CheckboxContainer,
	CheckboxText,
	Container,
	Form,
	FormActions,
	FormError,
	FormInput,
	FormLabel,
	FormTextarea,
	TicketTypesContainer,
} from './PunchupPoolDetails.styles';
import { FC, useEffect, useState } from 'react';
import { getData, post, put } from 'services/api';
import { useHistory, useParams } from 'react-router-dom';

import { PunchupButton } from 'features/common/components/Form/PunchupButton/PunchupButton';
import { PunchupShowDashboardPage } from 'features/common/components/Punchup/ShowDashboardPage/PunchupShowDashboardPage';
import { SectionTitle } from 'features/common/components/SectionTitle';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface PoolTicketType {
	ID: number;
	name: string;
}

interface PoolDetailsForm {
	name: string;
	description?: string;
	total_amount: number;
}

interface PoolDetailsApi extends PoolDetailsForm {
	ID: number;
	event_id: number;
	ticket_types?: PoolTicketType[];
}

const PunchupPoolDetails: FC = () => {
	const { t } = useTranslation();
	const { id: eventId, poolId } = useParams<{ id: string; poolId?: string }>();
	const history = useHistory();
	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, isSubmitting },
	} = useForm<PoolDetailsForm & { ticket_type_ids: string[] }>();
	const [selectedTicketTypeIds, setSelectedTicketTypeIds] = useState<string[]>([]);

	const isNewPool = !poolId;

	const eventTicketTypesQuery = useQuery(
		['event-ticket-types', eventId],
		async () => {
			const data = await getData(`events/${eventId}`);
			return data.ticket_types || [];
		},
		{ enabled: !!eventId }
	);

	useEffect(() => {
		if (!isNewPool && poolId) {
			getData(`/events/${eventId}/pools/${poolId}`).then((data: PoolDetailsApi) => {
				setValue('name', data.name);
				setValue('description', data.description || '');
				setValue('total_amount', data.total_amount);
				if (data.ticket_types) {
					const ids = data.ticket_types.map((tt) => tt.ID.toString());
					setSelectedTicketTypeIds(ids);
					setValue('ticket_type_ids', ids);
				}
			});
		}
	}, [poolId, setValue, isNewPool]);

	// Sync selectedTicketTypeIds with react-hook-form
	useEffect(() => {
		setValue('ticket_type_ids', selectedTicketTypeIds);
	}, [selectedTicketTypeIds, setValue]);

	const handleCheckboxChange = (id: string) => {
		setSelectedTicketTypeIds((prev) => (prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id]));
	};

	const onSubmit = async (data: PoolDetailsForm & { ticket_type_ids: string[] }) => {
		const payload = {
			...data,
			total_amount: Number(data.total_amount),
			ticket_type_ids: data.ticket_type_ids || [],
		};
		if (isNewPool && eventId) {
			await post(`/events/${eventId}/pools`, payload);
			history.push(`/home/dashboard/${eventId}`);
		} else if (poolId) {
			await put(`/events/${eventId}/pools/${poolId}`, payload);
			history.push(`/home/dashboard/${eventId}`);
		}
	};

	const onCancel = () => {
		history.push(`/home/dashboard/${eventId}`);
	};

	return (
		<PunchupShowDashboardPage pageName={isNewPool ? 'Add Pool' : 'Edit Pool'} onBack={onCancel}>
			<Container>
				<SectionTitle title={isNewPool ? t('poolDetails-create-title') : t('poolDetails-edit-title')} />
				<Form onSubmit={handleSubmit(onSubmit)}>
					<div style={{ marginBottom: 18 }}>
						<FormLabel>{t('poolDetails-name-label')}</FormLabel>
						<FormInput
							{...register('name', { required: t('poolDetails-name-required') })}
							placeholder={t('poolDetails-name-placeholder')}
						/>
						{errors.name && <FormError>{errors.name.message}</FormError>}
					</div>
					<div style={{ marginBottom: 18 }}>
						<FormLabel>{t('poolDetails-description-label')}</FormLabel>
						<FormTextarea {...register('description')} placeholder={t('poolDetails-description-placeholder')} />
					</div>
					<div style={{ marginBottom: 18 }}>
						<FormLabel>{t('poolDetails-ticketTypes-label')}</FormLabel>
						<TicketTypesContainer>
							{eventTicketTypesQuery.data?.map((tt: any) => {
								const isAttachedToOtherPool =
									tt.tickets_pool_id && (!poolId || tt.tickets_pool_id.toString() !== poolId);
								const tooltip = isAttachedToOtherPool ? t('poolDetails-ticketType-attached-tooltip') : '';
								return (
									<CheckboxContainer key={tt.ID}>
										<CheckboxButton
											checked={selectedTicketTypeIds.includes(tt.ID.toString())}
											onChange={() => handleCheckboxChange(tt.ID.toString())}
											disabled={isAttachedToOtherPool}
											title={tooltip}
										/>
										<CheckboxText title={tooltip}>{tt.name}</CheckboxText>
									</CheckboxContainer>
								);
							})}
						</TicketTypesContainer>
						{errors.ticket_type_ids && <FormError>{errors.ticket_type_ids.message as string}</FormError>}
					</div>
					<div style={{ marginBottom: 24 }}>
						<FormLabel>{t('poolDetails-total-label')}</FormLabel>
						<FormInput
							type="number"
							min={0}
							{...register('total_amount', {
								required: t('poolDetails-total-required'),
								min: { value: 0, message: t('poolDetails-total-min') },
							})}
							placeholder={t('poolDetails-total-placeholder')}
						/>
						{errors.total_amount && <FormError>{errors.total_amount.message}</FormError>}
					</div>
					<FormActions>
						<PunchupButton
							type="button"
							variant="secondary"
							text={t('button-cancel')}
							onClick={onCancel}
							style={{ flex: 1 }}
						/>
						<PunchupButton
							type="submit"
							variant="primary"
							disabled={isSubmitting}
							text={isNewPool ? t('button-create-new') : t('button-save')}
							style={{ flex: 1 }}
						/>
					</FormActions>
				</Form>
			</Container>
		</PunchupShowDashboardPage>
	);
};

export { PunchupPoolDetails };
